import { forwardRef, ReactNode, useMemo, useState } from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  Select as MantineSelect,
  SelectProps as MantineSelectProps
} from '@mantine/core';

import { InputLoader } from '../../feedback';
import { useMergeClassNames } from '../../utils';
import { SelectOverrideClassNames } from '../select';

interface BasicSelectProps extends MantineSelectProps {
  loading?: boolean;
}

/**
 * Wrapper over Mantine Select component lacks lot of props from v5, ideally for all use cases
 * Select can be used. This component can be renamed to Select incase mantine adds support for all
 * props we need in the future to the core Select component.
 */
const BasicSelect = forwardRef<HTMLInputElement, BasicSelectProps>((props, ref) => {
  const {
    size = 'md',
    leftSection,
    rightSection,
    loading,
    error,
    clearable,
    value,
    classNames,
    withCheckIcon = false,
    onFocus,
    onBlur,
    ...rest
  } = props;
  const [focussed, setFocussed] = useState<boolean>(false);

  let rightSectionFinal: ReactNode | undefined = undefined;
  let enableRightSectionEvents = true;
  if (loading) {
    rightSectionFinal = <InputLoader size={size} />;
    enableRightSectionEvents = false;
  } else if (error) {
    rightSectionFinal = <RiErrorWarningLine />;
    enableRightSectionEvents = false;
  } else if (clearable) {
    if (value) {
      rightSectionFinal = undefined;
      enableRightSectionEvents = false;
    } else {
      rightSectionFinal = <RiArrowDownSLine />;
    }
  } else if (rightSection) {
    rightSectionFinal = rightSection;
    enableRightSectionEvents = false;
  } else {
    rightSectionFinal = <RiArrowDownSLine />;
  }

  const classes = useMemo(() => {
    return SelectOverrideClassNames({
      ...props,
      rightSection: rightSectionFinal,
      focussed
    });
  }, [size, focussed, error, enableRightSectionEvents, rightSectionFinal]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(false);
    onBlur && onBlur(e);
  };

  const mergedClassNames = useMergeClassNames({
    classNames: [classes, classNames]
  });

  return (
    <MantineSelect
      {...rest}
      ref={ref}
      clearable={clearable}
      size={size}
      leftSection={leftSection}
      rightSection={rightSectionFinal}
      error={error}
      value={value}
      onFocus={handleFocus}
      onBlur={handleBlur}
      withCheckIcon={withCheckIcon}
      classNames={mergedClassNames}
    />
  );
});

export { BasicSelect };
export type { BasicSelectProps };
